import { BitkitButton, BitkitCheckbox, BitkitLink, BitkitTextInput } from '@bitrise/bitkit-v2';
import { chakra } from '@chakra-ui/react/styled-system';
import { datadogRum } from '@datadog/browser-rum';
import { AxiosError } from 'axios';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';

import { RevealablePasswordInput } from '@/components/RevealablePasswordInput/RevealablePasswordInput';
import useAlert from '@/hooks/useAlert';
import { getStandardErrorMessage } from '@/utils/errorResponseParser';
import { getDisplayableError } from '@/utils/formValidators/commonErrors';
import { navigateToUrl } from '@/utils/navigation';

import useSignIn, { SignInErrorResponse, SignInRequest } from '../hooks/useSignIn';

const InvalidCredentialsAlert = () => (
  <>
    The username or password you entered is incorrect. Did you{' '}
    <BitkitLink href="/users/forgot-password" textDecoration="underline">
      forget your password
    </BitkitLink>
    ?
  </>
);

const getCustomErrorMessage = (error: AxiosError<SignInErrorResponse>): ReactNode => {
  const status = error.response?.status;

  if (status === 401) {
    return <InvalidCredentialsAlert />;
  }

  if (status === 405) {
    return 'Your account has SSO login enforced by your organization. You can only log in via SAML SSO, please use SAML SSO to log in.';
  }

  return getStandardErrorMessage(error);
};

type FormDataType = SignInRequest['user'];

const SignInForm = ({
  rememberMe,
  onRememberMeChanged,
}: {
  rememberMe: boolean;
  onRememberMeChanged?: (value: boolean) => void;
}) => {
  const { setErrorAlert, removeAlert } = useAlert();
  const { mutate: submitSignIn, isPending, isSuccess } = useSignIn();
  const { register, handleSubmit, formState, watch } = useForm<FormDataType>({
    mode: 'onChange',
    defaultValues: {
      login: '',
    },
  });

  const { errors, isValid } = formState;
  const loginProps = register('login', { required: 'Email or username is required.' });
  const passwordProps = register('password', { required: 'Password is required.' });
  const login = watch('login');

  const onSubmit = handleSubmit(async (values) => {
    datadogRum.addAction('submit');

    removeAlert();

    const { password } = values;

    submitSignIn({ user: { login, password, rememberMe } } as SignInRequest, {
      onSuccess: (response) => {
        navigateToUrl(response.headers.location || '/');
      },
      onError: (error) => {
        if (error.response?.data?.code === 'unconfirmed_email') {
          navigateToUrl('/users/email-confirm');
          return;
        }

        setErrorAlert(getCustomErrorMessage(error));
      },
    });
  });

  const isSubmitDisabled = !isValid || isPending || isSuccess;

  return (
    <chakra.form onSubmit={onSubmit} display="flex" flexDirection="column" gap="16" noValidate>
      <BitkitTextInput
        label="Email or username"
        placeholder="Email or username"
        errorText={getDisplayableError(errors.login?.message, 'Email or username')}
        inputProps={{ ...loginProps, autoFocus: true }}
      />
      <RevealablePasswordInput
        label="Password"
        placeholder="Password"
        errorText={getDisplayableError(errors.password?.message, 'Password')}
        inputProps={passwordProps}
      />
      <BitkitCheckbox
        checked={rememberMe}
        labelText="Keep me logged in"
        onCheckedChange={(details) => onRememberMeChanged && onRememberMeChanged(details.checked === true)}
      />
      <BitkitButton
        type="submit"
        state={isPending ? 'loading' : isSubmitDisabled ? 'disabled' : undefined}
        width="100%"
      >
        Log in
      </BitkitButton>
    </chakra.form>
  );
};

export default SignInForm;
